import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import _ from 'lodash'
import { Link, NavLink } from 'react-router-dom'
import { Icon, Notification } from '@qure/containers'
import { useTranslation } from 'react-i18next'
import { useStoreState } from 'pullstate'
import UIStore from 'states/globalStore'
// import getAppVersion from 'libs/getAppVersion'
import getFeatureToggles from 'libs/getFeatureToggles'
import backendChecks from 'libs/backendChecks'
import miscUtils from 'libs/miscUtils'

import ChangePasswordModal from '../ChangePasswordModal'
import SiteSelector from '../SiteSelector'
import './index.scss'

const Menubar = props => {
  const { onLogout } = props
  const [isVisible, setVisible] = useState(false)
  const [isSiteSelectorVisible, setSiteSelectorVisible] = useState(false)
  const [hasSiteSelectedSuccess, setSelectionSuccess] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { userProfile, selectedSite } = useStoreState(UIStore, s => ({
    userProfile: s.userProfile,
    selectedSite: s.selectedSite
  }))

  const featureToggles = getFeatureToggles(userProfile)

  const { t } = useTranslation()

  const onToggleMenu = () => {
    setVisible(!isVisible)
  }

  const onToggleSiteSelectionSuccess = () => {
    setSelectionSuccess(true)
    setTimeout(() => {
      setSelectionSuccess(false)
    }, 5000)
  }

  useEffect(() => {
    onToggleSiteSelectionSuccess()
  }, [selectedSite])

  const onToggleSiteSelector = () => {
    setSiteSelectorVisible(!isSiteSelectorVisible)
  }

  const onUserLogout = e => {
    e.preventDefault()
    e.stopPropagation()
    onLogout(true)
    onToggleMenu()
  }

  const onOpenModal = () => {
    setIsModalOpen(true)
  }

  const onCloseModal = () => {
    setIsModalOpen(false)
  }

  const onSelectSiteCallback = () => {
    onToggleSiteSelector()
  }

  const { hasFeedback, hasTests } = getFeatureToggles(userProfile)

  const roles = backendChecks.getAllRoles(userProfile)

  const selectedSiteLabel = _.get(selectedSite, ['label'], '')
  const IS_MWL_ENABLED = miscUtils.getMwlState()

  return (
    <>
      <nav
        className="navbar is-fixed-top is-primary is-hidden-desktop is-hidden-tablet mobile-menubar"
        role="navigation"
        aria-label="main navigation"
      >
        <div className="navbar-brand">
          {/* eslint-disable */}
          <a
            onClick={onToggleMenu}
            onKeyDown={onToggleMenu}
            className={classNames('navbar-burger burger', {
              'is-active': isVisible
            })}
            aria-label="menu"
            aria-expanded="false"
            data-target="qureNavbar"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </a>
          <Link className="navbar-item logo-wrapper" to="/">
            <img
              src="/qTrack_logo.svg"
              width="112"
              height="28"
              alt="Qure Logo"
            />
          </Link>
          <a
            onClick={onToggleSiteSelector}
            onKeyDown={onToggleSiteSelector}
            className="navbar-item"
            aria-label="menu"
            aria-expanded="false"
            data-target="site-selector-dropdown"
          >
            <Icon iConClass="fa-map-marker-alt" />
            <span title={selectedSiteLabel}>
              {_.truncate(selectedSiteLabel, { length: '20' })}
            </span>
          </a>

          {/* eslint-enabled */}
        </div>
        <div
          id="site-selector-dropdown"
          className={classNames('navbar-menu', {
            'is-active': isSiteSelectorVisible
          })}
        >
          <div className="box is-shadowless has-text-primary has-text-centered">
            <SiteSelector onSelectSiteCallback={onSelectSiteCallback} />
          </div>
        </div>

        <div
          id="qureNavbar"
          className={classNames('navbar-menu', { 'is-active': isVisible })}
        >
          <div className="navbar-start">
            <NavLink
              to="/"
              exact
              className="navbar-item"
              activeClassName="is-active"
              onClick={onToggleMenu}
            >
              <Icon iConClass="fa-tachometer-alt" />
              <span>{t('Dashboard')}</span>
            </NavLink>
            <NavLink
              to="/client-lists"
              exact
              className="navbar-item"
              activeClassName="is-active"
              onClick={onToggleMenu}
            >
              <Icon iConClass="fa-list-alt" />
              <span>
                {backendChecks.translateClientName(
                  'View client list',
                  userProfile,
                  t
                )}
              </span>
            </NavLink>

            {roles.hasHealthWorkerRole && (
              <NavLink
                to="/add-client"
                exact
                className="navbar-item"
                activeClassName="is-active"
                onClick={onToggleMenu}
              >
                <Icon iConClass="fa-user-plus" />
                <span>
                  {backendChecks.translateClientName(
                    'Add new client',
                    userProfile,
                    t
                  )}
                </span>
              </NavLink>
            )}

            {roles.hasHealthWorkerRole &&
              (featureToggles.hasPngUpload ||
                featureToggles.hasDicomUpload) && (
                <NavLink
                  to="/uploads"
                  exact
                  className="navbar-item"
                  activeClassName="is-active"
                  onClick={onToggleMenu}
                >
                  <Icon iConClass="fa-cloud-upload-alt" />
                  <span>{t('Manage uploads')}</span>
                </NavLink>
              )}

            {hasFeedback && roles.hasRadiologistRole && (
              <NavLink
                to="/radiologist-lists"
                exact
                className="navbar-item"
                activeClassName="is-active"
                onClick={onToggleMenu}
              >
                <Icon iConClass="fa-list-alt" />
                <span>{t('View Radiologist feedback')}</span>
              </NavLink>
            )}

            {hasTests && roles.hasHealthWorkerRole && (
              <NavLink
                to="/tests-lists"
                exact
                className="navbar-item"
                activeClassName="is-active"
                onClick={onToggleMenu}
              >
                <Icon iConClass="fa-list-alt" />
                <span>{t('View lab confirmations')}</span>
              </NavLink>
            )}

            {roles.hasAdminRole && (
              <NavLink
                to="/manage-users"
                exact
                className="navbar-item"
                activeClassName="is-active"
                onClick={onToggleMenu}
              >
                <Icon iConClass="fa-users-cog" />
                <span>{t('Manage users')}</span>
              </NavLink>
            )}

            {roles.hasAdminRole && (
              <NavLink
                to="/manage-sites"
                exact
                className="navbar-item"
                activeClassName="is-active"
                onClick={onToggleMenu}
              >
                <Icon iConClass="fa-users-cog" />
                <span>{t('Manage sites')}</span>
              </NavLink>
            )}

            <a
              href="/terms-and-conditions-en.html"
              target="_blank"
              rel="noreferrer"
              className="navbar-item"
            >
              <Icon iConClass="fa-external-link-alt" />
              <span>{t('Terms and conditions')}</span>
            </a>
            <a className="navbar-item" onClick={onOpenModal}>
              <Icon iConClass="fa-lock" />
              <span>{t('Change password')}</span>
            </a>
            <a href="#" className="navbar-item" onClick={onUserLogout}>
              <Icon iConClass="fa-sign-out-alt" />
              <span>{t('Logout')}</span>
            </a>
            {IS_MWL_ENABLED && (
              <a>
                <Icon iConClass="fa-computer-classic" />
                <span>{t('Configure modality')}</span>
              </a>
            )}
            {/* <div className="navbar-item app-version">{getAppVersion()}</div> */}
          </div>
        </div>
      </nav>

      <ChangePasswordModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        isAdmin={false}
      />
      <Notification
        color="primary"
        className={classNames(
          'site-selection-notification box is-hidden-tablet',
          { 'is-open': hasSiteSelectedSuccess }
        )}
      >
        {t('Selected site')}
        <span className="has-text-weight-bold"> {selectedSiteLabel}</span>
      </Notification>
    </>
  )
}

Menubar.defaultProps = {}

Menubar.propTypes = {
  onLogout: PropTypes.func.isRequired
}

export default Menubar
